import {
  CopyToClipboardButton,
  ErrorBox,
  Loading,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@gearbox-protocol/deploy-tools-ui";
import { getChain, shortAddress } from "@gearbox-protocol/sdk/onchain";
import { type FC, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { type Hex, isHash } from "viem";

import { useUnpausedForkStatus } from "../context";
import { CreditAccountOperations } from "./CreditAccountOperations";
import { EventList } from "./EventList";
import { useParseTx } from "./useParseTx";

const OPERATIONS_TAB = "operations";
const EVENTS_TAB = "events";
type TabValue = typeof OPERATIONS_TAB | typeof EVENTS_TAB;

const TxRoute: FC = () => {
  const { txHash } = useParams<{ txHash: string }>();
  const fork = useUnpausedForkStatus();

  if (!txHash || !isHash(txHash)) {
    return (
      <div className="rounded-xl bg-background p-4">
        <ErrorBox>{`Invalid transaction hash: ${txHash ?? ""}`}</ErrorBox>
      </div>
    );
  }

  return (
    <TxRouteInner txHash={txHash as Hex} chainId={getChain(fork.network).id} />
  );
};

const TxRouteInner: FC<{ txHash: Hex; chainId: number }> = ({
  txHash,
  chainId,
}) => {
  const fork = useUnpausedForkStatus();
  const { value, loading, error } = useParseTx(txHash);
  const trace = `cast run --rpc-url ${fork.rpcUrl} ${txHash}`;

  const { hash } = useLocation();
  const navigate = useNavigate();
  const tab: TabValue = hash === `#${EVENTS_TAB}` ? EVENTS_TAB : OPERATIONS_TAB;
  const setTab = useCallback(
    (next: string) => navigate({ hash: `#${next}` }, { replace: true }),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-background p-4">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-baseline gap-2">
          <h2>Parsed transaction</h2>
          <code className="text-xs text-gray-400">{shortAddress(txHash)}</code>
        </div>

        <CopyToClipboardButton text={trace}>
          Copy trace Call
        </CopyToClipboardButton>
      </div>

      {loading && <Loading />}
      {error && <ErrorBox error={error} />}

      {value && (
        <>
          <div className="flex flex-row gap-4 text-xs text-gray-400 flex-wrap">
            <div>
              Credit account:{" "}
              <code className="text-gray-200">{value.creditAccount}</code>
            </div>
            <div>
              Credit facade:{" "}
              <code className="text-gray-200">{value.creditFacade}</code>
            </div>
            <div>
              Block:{" "}
              <code className="text-gray-200">
                {value.blockNumber.toString()}
              </code>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value={OPERATIONS_TAB}>Operations</TabsTrigger>
              <TabsTrigger value={EVENTS_TAB}>Events</TabsTrigger>
            </TabsList>
            <TabsContent value={OPERATIONS_TAB}>
              <CreditAccountOperations
                ops={value.ops}
                underlyingToken={value.underlying}
                tokens={value.tokens}
                protocols={value.protocols}
                chainId={chainId}
              />
            </TabsContent>
            <TabsContent value={EVENTS_TAB}>
              <EventList events={value.events} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default TxRoute;
